import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Row, Col, Card, Statistic, Typography, Progress, Button, Space, Avatar, List, Spin, Alert, Tag, Select, DatePicker } from 'antd';
import {
  TeamOutlined,
  WalletOutlined,
  CreditCardOutlined,
  CalendarOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  EyeOutlined,
  RiseOutlined,
  SaveOutlined,
  QuestionOutlined,
  GroupOutlined,
  GlobalOutlined,
  DownloadOutlined,
  FilterOutlined,
  DollarOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import useDashboard from '../../hooks/useDashboard';
import { useSystemDashboard } from '../../hooks/useSystemAnalytics';
import { useAuthContext } from '../../contexts/AuthContext';
import { useCommune } from '../../hooks/useCommune';
import communeService from '../../services/commune.service';
import type { SystemAnalyticsFilters } from '../../services/systemAnalytics.service';
import dayjs from 'dayjs';
import EventAvailableOutlined from "@ant-design/icons";
import TrendingUpOutlined from "@ant-design/icons";
import AccountBalanceWalletOutlined from "@ant-design/icons";


const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const DashboardPage: React.FC = () => {
  const { isSuperAdmin, isCommunalAdmin, getAssignedCommuneId, user } = useAuthContext();
  
  // Get commune information for communal admins
  const assignedCommuneId = getAssignedCommuneId();
  const { commune, loading: communeLoading } = useCommune(assignedCommuneId);
  const [filters, setFilters] = useState<SystemAnalyticsFilters>({
    startDate: dayjs().subtract(30, 'days').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
  });
  
  // Income summary data for communal admins
  const [incomeSummary, setIncomeSummary] = useState<any>(null);
  const [incomeSummaryLoading, setIncomeSummaryLoading] = useState(false);
  
  // Stable values to prevent unnecessary re-renders
  const isCommunalAdminStable = isCommunalAdmin();
  const isSuperAdminStable = isSuperAdmin();

  // Use different data sources based on user role  
  const isSystemAdmin = isSuperAdminStable;
  
  // Always call both hooks but with stable parameters
  const stableFilters = useMemo(() => ({}), []);
  const regularDashboard = useDashboard();
  const systemDashboard = useSystemDashboard(stableFilters);

  // Select the appropriate dashboard data based on user role
  const { data, loading, error } = isSystemAdmin ? systemDashboard : regularDashboard;
  const refresh = isSystemAdmin ? systemDashboard.refetch : regularDashboard.refresh;

  // Handle filter changes for super admin - memoized to prevent unnecessary re-renders
  const handleFilterChange = useCallback((key: keyof SystemAnalyticsFilters, value: any) => {
    if (isSystemAdmin) {
      setFilters(prev => ({ ...prev, [key]: value }));
    }
  }, [isSystemAdmin]);

  const handleDateRangeChange = useCallback((dates: any) => {
    if (dates && dates[0] && dates[1] && isSystemAdmin) {
      setFilters(prev => ({
        ...prev,
        startDate: dates[0].format('YYYY-MM-DD'),
        endDate: dates[1].format('YYYY-MM-DD')
      }));
    }
  }, [isSystemAdmin]);

  // Fetch income summary for communal admins - only once on mount and when user changes
  useEffect(() => {
    const fetchIncomeSummary = async () => {
      if (!isCommunalAdminStable) return;
      if (!assignedCommuneId) return;
      
      setIncomeSummaryLoading(true);
      try {
        const response = await communeService.getCommuneIncomeSummary(assignedCommuneId);
        console.log('🔍 INCOME SUMMARY - Full response:', response);
        
        // Handle response directly as the data object, not wrapped in success/data structure
        if (response && response.communeId) {
          console.log('🔍 INCOME SUMMARY - Extracted data:', response);
          console.log('🔍 INCOME SUMMARY - Activities income:', response?.activitiesIncome);
          console.log('🔍 INCOME SUMMARY - Other income:', response?.otherIncome);
          console.log('🔍 INCOME SUMMARY - Total income:', response?.totalIncomeAmount);
          setIncomeSummary(response);
        } else {
          console.log('🔍 INCOME SUMMARY - Invalid response format:', response);
        }
      } catch (err) {
        console.error('Income summary fetch failed:', err);
      } finally {
        setIncomeSummaryLoading(false);
      }
    };

    fetchIncomeSummary();
  }, [isCommunalAdminStable, assignedCommuneId]); // Removed 'data' dependency to prevent loops

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh',
        background: '#f8fafc'
      }}>
        <Spin size="large" tip="Chargement des données du tableau de bord..." />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', padding: '24px' }}>
        <Alert
          message="Erreur du tableau de bord"
          description={`Error loading dashboard data: ${error.message}`}
          type="error"
          showIcon
          style={{ margin: '16px 0', borderRadius: '12px' }}
          action={
            <Button onClick={refresh} type="primary">
              Réessayer
            </Button>
          }
        />
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ minHeight: '100vh', padding: '24px' }}>
        <Alert
          message="Aucune donnée disponible"
          description="Aucune donnée de tableau de bord disponible. Veuillez actualiser ou contacter le support."
          type="info"
          showIcon
          style={{ margin: '16px 0', borderRadius: '12px' }}
          action={
            <Button onClick={refresh} type="primary">
              Refresh
            </Button>
          }
        />
      </div>
    );
  }

  // Handle different data structures for super admin vs regular users
  let adaptedKpis, adaptedSavingsStats, adaptedCreditStats, adaptedMemberStats, adaptedSolidarityStats, adaptedMeetingStats;
  
  // Define actualData for regular users (will be used later in KPI cards)
  const actualData = data?.data || data || {};
  
  if (isSystemAdmin && data) {
    // System-wide data structure for super admins
    const systemData = data as any;
    
    // Debug logging
    console.log('System Data Structure:', systemData);
    console.log('Overview data:', systemData.overview);
    console.log('Savings data:', systemData.savings);
    console.log('Credits data:', systemData.credits);
    console.log('Full data object:', data);
    console.log('Is system admin:', isSystemAdmin);
    
    adaptedKpis = {
      totalMembers: systemData.overview?.totalMembersAcrossCommunes || 0,
      activeMembers: systemData.overview?.totalMembersAcrossCommunes || 0,
      totalSavings: systemData.savings?.totalAmount || 0,
      totalCredits: systemData.credits?.totalAmount || 0,
      solidarityFund: systemData.solidarityContributions?.totalAmount || 0,
      solidarityFundBalance: systemData.solidarityContributions?.totalAmount || 0,
      monthlyGrowth: {
        members: Math.floor(Math.random() * 20) - 5, // Simulated for now
        savings: Math.floor(Math.random() * 15),
        credits: Math.floor(Math.random() * 10)
      }
    };

    adaptedSavingsStats = {
      totalTransactions: systemData.savings?.totalTransactions || 0,
      completedTransactions: Math.floor((systemData.savings?.totalTransactions || 0) * 0.85),
      pendingTransactions: Math.floor((systemData.savings?.totalTransactions || 0) * 0.15),
      netSavings: systemData.savings?.totalAmount || 0
    };

    adaptedCreditStats = {
      totalCredits: systemData.credits?.totalCredits || 0,
      totalOutstanding: systemData.credits?.totalOutstanding || 0,
      pendingCredits: systemData.credits?.statusBreakdown?.find((s: any) => s.status === 'pending')?.count || 0,
      activeCredits: systemData.credits?.statusBreakdown?.find((s: any) => s.status === 'active')?.count || 0,
      completedCredits: systemData.credits?.statusBreakdown?.find((s: any) => s.status === 'completed')?.count || 0,
      repaymentRate: systemData.credits?.totalAmount > 0 ? 
        Math.round(((systemData.credits?.totalAmount - systemData.credits?.totalOutstanding) / systemData.credits?.totalAmount) * 100) : 0
    };

    adaptedMemberStats = {
      totalMembers: systemData.overview?.totalMembersAcrossCommunes || 0,
      activeMembers: systemData.overview?.totalMembersAcrossCommunes || 0,
      newMembers: Math.floor((systemData.overview?.totalMembersAcrossCommunes || 0) * 0.1), // Simulated
      active: Math.floor((systemData.overview?.totalMembersAcrossCommunes || 0) * 0.9),
      pending: Math.floor((systemData.overview?.totalMembersAcrossCommunes || 0) * 0.05),
      suspended: Math.floor((systemData.overview?.totalMembersAcrossCommunes || 0) * 0.03),
      inactive: Math.floor((systemData.overview?.totalMembersAcrossCommunes || 0) * 0.02)
    };

    adaptedSolidarityStats = {
      totalFund: systemData.solidarityContributions?.totalAmount || 0,
      totalRequests: Math.floor((systemData.solidarityContributions?.totalContributions || 0) * 0.2), // Simulated
      pendingRequests: Math.floor((systemData.solidarityContributions?.totalContributions || 0) * 0.05),
      completedRequests: Math.floor((systemData.solidarityContributions?.totalContributions || 0) * 0.15)
    };

    adaptedMeetingStats = {
      totalMeetings: Math.floor((systemData.overview?.totalGroupsAcrossCommunes || 0) * 4), // Simulated: ~4 meetings per group
      scheduledMeetings: Math.floor((systemData.overview?.totalGroupsAcrossCommunes || 0) * 0.5),
      completedMeetings: Math.floor((systemData.overview?.totalGroupsAcrossCommunes || 0) * 3.5)
    };
  } else {
    // Regular user data structure (existing logic)
    const { kpis, memberStats, savingsStats, creditStats, solidarityStats, meetingStats, overview, financial, membership } = actualData;
    
    adaptedKpis = kpis || (overview ? {
      totalMembers: overview.totalMembers || 0,
      activeMembers: overview.totalMembers || 0,
      totalSavings: overview.totalSavings || 0,
      totalCredits: overview.totalCredits || 0,
      solidarityFund: overview.totalGroupFunds || 0,
      solidarityFundBalance: overview.totalGroupFunds || 0,
      monthlyGrowth: {
        members: 0,
        savings: 0,
        credits: 0
      }
    } : {
      totalMembers: 0,
      activeMembers: 0,
      totalSavings: 0,
      totalCredits: 0,
      solidarityFund: 0,
      solidarityFundBalance: 0,
      monthlyGrowth: { members: 0, savings: 0, credits: 0 }
    });

    adaptedSavingsStats = savingsStats || {
      totalTransactions: 0,
      completedTransactions: 0,
      pendingTransactions: 0,
      netSavings: financial?.currentBalances?.totalSavings || 0
    };

    adaptedCreditStats = creditStats || {
      totalCredits: overview?.totalCredits || 0,
      totalOutstanding: overview?.totalCredits || 0,
      pendingCredits: 0,
      activeCredits: 0,
      completedCredits: 0,
      repaymentRate: 85
    };

    adaptedMemberStats = memberStats || membership?.stats || {
      totalMembers: overview?.totalMembers || 0,
      activeMembers: overview?.totalMembers || 0,
      newMembers: membership?.growth?.newMembers || 0
    };

    adaptedSolidarityStats = solidarityStats || {
      totalFund: overview?.totalGroupFunds || 0,
      totalRequests: 0,
      pendingRequests: 0,
      completedRequests: 0
    };

    adaptedMeetingStats = meetingStats || {
      totalMeetings: 0,
      scheduledMeetings: 0,
      completedMeetings: 0
    };
  }

  // Modern StatCard component matching other pages
  const StatCard = ({ 
    title, 
    value, 
    prefix, 
    suffix = '', 
    growth, 
    color, 
    description, 
    icon 
  }: {
    title: string;
    value: string | number;
    prefix?: React.ReactNode;
    suffix?: string;
    growth?: number;
    color: string;
    description?: string;
    icon?: React.ReactNode;
  }) => {
    return (
      <Card
        style={{
          background: '#ffffff',
          border: '1px solid #e5e7eb',
          borderRadius: '16px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s ease',
          overflow: 'hidden',
          position: 'relative',
          height: '100%',
        }}
        bodyStyle={{ padding: '24px' }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
        }}
      >
        {/* Accent border */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '4px',
            height: '100%',
            background: color,
          }}
        />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: '14px', fontWeight: '500' }}>
              {title}
            </Text>
            <div style={{ 
              fontSize: '28px', 
              fontWeight: '700', 
              margin: '8px 0',
              color: '#0f172a'
            }}>
              {prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}
            </div>
            {growth !== undefined && (
              <div style={{ display: 'flex', alignItems: 'center', fontSize: '13px' }}>
                <span style={{ 
                  color: growth >= 0 ? '#059669' : '#dc2626', 
                  marginRight: '6px',
                  fontWeight: '600'
                }}>
                  {growth >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                  {Math.abs(growth)}%
                </span>
                <Text type="secondary" style={{ fontSize: '13px' }}>
                  {description || 'vs last month'}
                </Text>
              </div>
            )}
            {description && growth === undefined && (
              <Text type="secondary" style={{ fontSize: '13px' }}>
                {description}
              </Text>
            )}
          </div>
          {icon && (
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '28px',
              backgroundColor: `${color}15`,
              color: color,
              marginLeft: '16px'
            }}>
              {icon}
            </div>
          )}
        </div>
      </Card>
    );
  };

  // Generate recent activities from the data
  const recentActivities = [
    {
      id: 1,
      member: 'Activités récentes des membres',
      action: 'Transactions d\'épargne',
      amount: adaptedSavingsStats.completedTransactions,
      time: 'Aujourd\'hui',
      type: 'savings'
    },
    {
      id: 2,
      member: 'Demandes de prêts',
      action: 'En attente de relecture',
      amount: adaptedCreditStats.pendingCredits,
      time: 'Cette semaine',
      type: 'loan'
    },
    {
      id: 3,
      member: 'Rendez-vous à venir',
      action: 'Programmés',
      amount: adaptedMeetingStats.scheduledMeetings,
      time: 'Cette mois',
      type: 'meeting'
    },
    {
      id: 4,
      member: 'Demandes de solidarité',
      action: 'En attente d\'approbation',
      amount: adaptedSolidarityStats.pendingRequests,
      time: 'Cette semaine',
      type: 'solidarity'
    },
  ];

  return (
    <div style={{ minHeight: '100vh' }}>
      {/* Welcome Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        marginBottom: '32px' 
      }}>
        <div>
          <Title level={2} style={{ margin: 0, color: '#1a1a1a', fontSize: '36px' }}>
            Aperçu du tableau de bord
          </Title>
          <Text style={{ color: '#666', fontSize: '18px' }}>
            {isCommunalAdminStable && commune ? (
              <>
                Surveillez les performances de la commune <strong style={{ color: '#52c41a' }}>{commune.communeName}</strong>
              </>
            ) : isCommunalAdminStable && communeLoading ? (
              'Chargement des informations de commune...'
            ) : (
              'Surveillez les performances et les indicateurs clés de vos groupes AVEC'
            )}
          </Text>
          {isCommunalAdminStable && commune && (
            <div style={{ marginTop: '8px' }}>
              <Tag 
                color="green" 
                style={{ 
                  fontSize: '14px', 
                  padding: '4px 12px', 
                  borderRadius: '6px',
                  fontWeight: '600'
                }}
                icon={<GlobalOutlined />}
              >
                {commune.communeName} - {commune.communeCode}
              </Tag>
            </div>
          )}
        </div>
        <Button
          type="primary"
          icon={<RiseOutlined />}
          size="large"
          onClick={refresh}
          style={{
            borderRadius: '12px',
            background: '#374151',
            border: 'none',
            height: '48px',
            fontSize: '16px',
            fontWeight: '600',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          }}
        >
          Actualiser les données
        </Button>
      </div>

      {/* Main KPI Cards */}
      <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Total des groupes"
            value={isSystemAdmin 
              ? (data?.overview?.totalGroupsAcrossCommunes || 0)
              : (actualData?.overview?.totalGroups || actualData?.totalGroups || 0)
            }
            color="#3b82f6"
            description="Groupes actifs"
            icon={<GroupOutlined />}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Épargne totale"
            value={(parseFloat(adaptedKpis.totalSavings) || 0).toLocaleString()}
            suffix=" CFA"
            color="#059669"
            growth={adaptedKpis.monthlyGrowth.savings}
            description="Croissance mensuelle"
            icon={<WalletOutlined />}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Crédits en cours"
            value={(adaptedCreditStats.totalOutstanding || 0).toLocaleString()}
            suffix=" CFA"
            color="#7c3aed"
            growth={adaptedCreditStats.repaymentRate - 85} // Mock growth based on repayment rate
            description="Performance du portefeuille"
            icon={<CreditCardOutlined />}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Fonds de solidarité"
            value={(parseFloat(adaptedKpis.solidarityFundBalance) || 0).toLocaleString()}
            suffix=" CFA"
            color="#d97706"
            description="Fonds de soutien communautaire"
            icon={<QuestionOutlined />}
          />
        </Col>
      </Row>

      {/* Enhanced Dashboard Section for Communal Admins */}
      {isCommunalAdminStable && (
        <>
          {/* Activity Income Section */}
          <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
            <Col xs={24}>
              <Title level={3} style={{ margin: '0 0 16px 0', color: '#1a1a1a', fontSize: '24px' }}>
                <TrophyOutlined style={{ marginRight: '8px', color: '#52c41a' }} />
                Revenus d'Activités
              </Title>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <StatCard
                title="Revenus d'activités"
                value={incomeSummaryLoading ? '...' : (incomeSummary?.activitiesIncome || actualData?.financial?.activityIncome || 0).toLocaleString()}
                suffix=" CFA"
                color="#52c41a"
                description="Revenus des activités de groupe"
                icon={<TrophyOutlined />}
              />
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <StatCard
                title="Autres revenus"
                value={incomeSummaryLoading ? '...' : (incomeSummary?.otherIncome || 0).toLocaleString()}
                suffix=" CFA"
                color="#722ed1"
                description="Revenus hors activités"
                icon={<WalletOutlined />}
              />
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <StatCard
                title="Total des revenus"
                value={incomeSummaryLoading ? '...' : (incomeSummary?.totalIncomeAmount || actualData?.financial?.activityIncome || 0).toLocaleString()}
                suffix=" CFA"
                color="#1890ff"
                description="Revenus combinés"
                icon={<DollarOutlined />}
              />
            </Col>
          </Row>

        </>
      )}

      {/* Secondary Metrics */}
      <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
        <Col xs={24} md={8}>
          <Card
            title={
              <Space>
                <AccountBalanceWalletOutlined style={{ color: '#6b7280' }} />
                <span>Statistiques des membres</span>
              </Space>
            }
            style={{
              borderRadius: '16px',
              background: '#ffffff',
              border: '1px solid #e5e7eb',
              height: '100%'
            }}
          >
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic
                  title="Actifs"
                  value={adaptedMemberStats.active}
                  valueStyle={{ color: '#059669', fontSize: '24px' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="En attente"
                  value={adaptedMemberStats.pending}
                  valueStyle={{ color: '#d97706', fontSize: '24px' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Suspendus"
                  value={adaptedMemberStats.suspended}
                  valueStyle={{ color: '#dc2626', fontSize: '24px' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Inactifs"
                  value={adaptedMemberStats.inactive}
                  valueStyle={{ color: '#6b7280', fontSize: '24px' }}
                />
              </Col>
            </Row>
          </Card>
        </Col>
        
        <Col xs={24} md={8}>
          <Card
            title={
              <Space>
                <SaveOutlined style={{ color: '#6b7280' }} />
                <span>Aperçu de l'épargne</span>
              </Space>
            }
            style={{
              borderRadius: '16px',
              background: '#ffffff',
              border: '1px solid #e5e7eb',
              height: '100%'
            }}
          >
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <div>
                <Text style={{ fontSize: '14px', color: '#666', fontWeight: '500' }}>
                  Total des transactions: {adaptedSavingsStats.totalTransactions}
                </Text>
              </div>
              <div>
                <Text style={{ fontSize: '14px', color: '#666', fontWeight: '500' }}>
                  Terminées: {adaptedSavingsStats.completedTransactions}
                </Text>
                <Progress 
                  percent={adaptedSavingsStats.totalTransactions > 0 ? Math.round((adaptedSavingsStats.completedTransactions / adaptedSavingsStats.totalTransactions) * 100) : 0} 
                  showInfo={false} 
                  strokeColor="#059669"
                  style={{ marginTop: '4px' }}
                />
              </div>
              <div>
                <Text style={{ fontSize: '14px', color: '#666', fontWeight: '500' }}>
                  En attente: {adaptedSavingsStats.pendingTransactions}
                </Text>
              </div>
              <div>
                <Text style={{ fontSize: '16px', fontWeight: '600', color: '#0f172a' }}>
                  Épargne nette: {(adaptedSavingsStats.netSavings || 0).toLocaleString()} CFA
                </Text>
              </div>
            </Space>
          </Card>
        </Col>
        
        <Col xs={24} md={8}>
          <Card
            title={
              <Space>
                <TrendingUpOutlined style={{ color: '#6b7280' }} />
                <span>Portefeuille de crédit</span>
              </Space>
            }
            style={{
              borderRadius: '16px',
              background: '#ffffff',
              border: '1px solid #e5e7eb',
              height: '100%'
            }}
          >
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <div>
                <Text style={{ fontSize: '14px', fontWeight: '500', color: '#666' }}>
                  Taux de remboursement
                </Text>
                <div style={{ marginTop: '8px' }}>
                  <Progress 
                    type="circle" 
                    percent={adaptedCreditStats.repaymentRate} 
                    size={80}
                    strokeColor="#7c3aed"
                    format={(percent) => `${percent}%`}
                  />
                </div>
              </div>
              
              <Row gutter={8}>
                <Col span={12}>
                  <Text style={{ fontSize: '12px', color: '#666' }}>Taux de défaut</Text>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#dc2626' }}>
                    {adaptedCreditStats.defaultRate}%
                  </div>
                </Col>
                <Col span={12}>
                  <Text style={{ fontSize: '12px', color: '#666' }}>Prêts actifs</Text>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#059669' }}>
                    {adaptedCreditStats.activeCredits}
                  </div>
                </Col>
              </Row>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Group Performance Section for Communal Admins */}
      {/* {isCommunalAdmin() && actualData?.groupPerformance && (
        <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
          <Col xs={24}>
            <Card
              title={
                <Space>
                  <GroupOutlined style={{ color: '#6b7280' }} />
                  <span style={{ fontSize: '18px', fontWeight: '600' }}>Performance des Groupes</span>
                </Space>
              }
              style={{
                borderRadius: '16px',
                background: '#ffffff',
                border: '1px solid #e5e7eb',
              }}
            >
              <List
                dataSource={actualData.groupPerformance.slice(0, 5)}
                renderItem={(group: any) => (
                  <List.Item
                    style={{
                      padding: '16px 0',
                      borderBottom: '1px solid #f1f5f9',
                    }}
                  >
                    <List.Item.Meta
                      avatar={
                        <Avatar
                          style={{
                            background: '#52c41a',
                            fontSize: '16px'
                          }}
                        >
                          <GroupOutlined />
                        </Avatar>
                      }
                      title={
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: '600', color: '#0f172a' }}>{group.groupName}</span>
                          <div style={{ textAlign: 'right' }}>
                            <Text style={{ fontWeight: '700', color: '#374151', fontSize: '14px', display: 'block' }}>
                              {group.totalMembers} membres
                            </Text>
                            <Text style={{ color: '#52c41a', fontSize: '12px' }}>
                              {new Intl.NumberFormat('fr-TG', { style: 'currency', currency: 'XOF' }).format(group.totalSavings || 0)}
                            </Text>
                          </div>
                        </div>
                      }
                      description={
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#6b7280' }}>
                            Épargne: {new Intl.NumberFormat('fr-TG', { style: 'currency', currency: 'XOF' }).format(group.avgSavingsPerMember || 0)}/membre
                          </span>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            Score: {Math.round((group.performanceScore || 0) * 100)}%
                          </Text>
                        </div>
                      }
                    />
                  </List.Item>
                )}
                locale={{
                  emptyText: 'Aucune donnée de performance de groupe disponible'
                }}
              />
            </Card>
          </Col>
        </Row>
      )} */}

      {/* Activity and Performance Section */}
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={isCommunalAdminStable ? 12 : 24}>
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Space>
                  <EventAvailableOutlined style={{ color: '#6b7280' }} />
                  <span style={{ fontSize: '18px', fontWeight: '600' }}>Activité du système</span>
                </Space>
                <Button type="link" icon={<EyeOutlined />} style={{ color: '#6b7280' }}>
                  Voir les détails
                </Button>
              </div>
            }
            style={{
              borderRadius: '16px',
              background: '#ffffff',
              border: '1px solid #e5e7eb',
            }}
          >
            <List
              dataSource={recentActivities}
              renderItem={(item) => (
                <List.Item
                  style={{
                    padding: '16px 0',
                    borderBottom: '1px solid #f1f5f9',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        style={{
                          background: item.type === 'savings' ? '#059669' : 
                                     item.type === 'loan' ? '#7c3aed' : 
                                     item.type === 'meeting' ? '#0284c7' : '#d97706',
                          fontSize: '16px'
                        }}
                      >
                        {item.type === 'savings' ? <WalletOutlined /> :
                         item.type === 'loan' ? <CreditCardOutlined /> :
                         item.type === 'meeting' ? <CalendarOutlined /> : 
                         <QuestionOutlined />}
                      </Avatar>
                    }
                    title={
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: '600', color: '#0f172a' }}>{item.member}</span>
                        <Text style={{ fontWeight: '700', color: '#374151', fontSize: '16px' }}>
                          {item.amount}
                        </Text>
                      </div>
                    }
                    description={
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#6b7280' }}>{item.action}</span>
                        <Text type="secondary" style={{ fontSize: '12px' }}>{item.time}</Text>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
        
        {/* Membership Analytics for Communal Admins */}
        {isCommunalAdminStable && actualData?.membership && (
          <Col xs={24} lg={12}>
            <Card
              title={
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Space>
                    <TeamOutlined style={{ color: '#6b7280' }} />
                    <span style={{ fontSize: '18px', fontWeight: '600' }}>Analyse des Membres</span>
                  </Space>
                  <Button type="link" icon={<EyeOutlined />} style={{ color: '#6b7280' }}>
                    Voir détails
                  </Button>
                </div>
              }
              style={{
                borderRadius: '16px',
                background: '#ffffff',
                border: '1px solid #e5e7eb',
              }}
            >
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Statistic
                    title="Nouveaux membres"
                    value={actualData.membership.growth?.newMembers || 0}
                    valueStyle={{ color: '#52c41a', fontSize: '20px' }}
                    prefix={<ArrowUpOutlined />}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Taux de rétention"
                    value={Math.round((actualData.membership.retention?.rate || 0) * 100)}
                    suffix="%"
                    valueStyle={{ color: '#1890ff', fontSize: '20px' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Membres actifs"
                    value={actualData.membership.stats?.active || 0}
                    valueStyle={{ color: '#722ed1', fontSize: '20px' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="En attente"
                    value={actualData.membership.stats?.pending || 0}
                    valueStyle={{ color: '#fa8c16', fontSize: '20px' }}
                  />
                </Col>
              </Row>
              
              {/* Demographics */}
              {actualData.membership.demographics && (
                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
                  <Text strong style={{ fontSize: '14px' }}>Démographie:</Text>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
                    <Text type="secondary">
                      Âge moyen: {Math.round(actualData.membership.demographics.averageAge || 0)} ans
                    </Text>
                    <Text type="secondary">
                      Femmes: {Math.round((actualData.membership.demographics.genderDistribution?.female || 0) * 100)}%
                    </Text>
                  </div>
                </div>
              )}
            </Card>
          </Col>
        )}
      </Row>

      {/* Financial Trends for Communal Admins */}
      {isCommunalAdminStable && actualData?.trends && (
        <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
          <Col xs={24}>
            <Card
              title={
                <Space>
                  <RiseOutlined style={{ color: '#6b7280' }} />
                  <span style={{ fontSize: '18px', fontWeight: '600' }}>Tendances Financières (30 derniers jours)</span>
                </Space>
              }
              style={{
                borderRadius: '16px',
                background: '#ffffff',
                border: '1px solid #e5e7eb',
              }}
            >
              <Row gutter={[16, 16]}>
                {Object.entries(actualData.trends).slice(-4).map(([date, data]: [string, any]) => (
                  <Col xs={24} sm={12} lg={6} key={date}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <Text strong style={{ fontSize: '12px', color: '#666' }}>
                        {new Date(date).toLocaleDateString('fr-FR', { month: 'short', day: 'numeric' })}
                      </Text>
                      <div style={{ margin: '8px 0' }}>
                        <Text style={{ fontSize: '16px', fontWeight: '600', color: '#52c41a', display: 'block' }}>
                          {new Intl.NumberFormat('fr-TG', { 
                            style: 'currency', 
                            currency: 'XOF',
                            notation: 'compact' 
                          }).format(data.totalSavings || 0)}
                        </Text>
                        <Text type="secondary" style={{ fontSize: '11px' }}>Épargnes</Text>
                      </div>
                      <div>
                        <Text style={{ fontSize: '14px', color: '#1890ff' }}>
                          {data.totalMembers || 0} membres
                        </Text>
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card>
          </Col>
        </Row>
      )}

    </div>
  );
};

export default DashboardPage;